import { create as createExternalComment, findComments as findExternalComments } from "@synthesis/shared-services/comments/external/client";
import type { CommentDto } from "@synthesis/shared-services/comments/external/types";
import { create as createInternalComment, findComments as findInternalComments } from "@synthesis/shared-services/comments/internal/client";
import { setPromiseSnack } from "@synthesis/shared-ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { useClientType } from "../../../shared/hooks/use-client-type";
import { useUserInfo } from "../../../shared/hooks/use-user-info";
import {
  APPLICATION_COMMENTS_COUNT_QUERY_KEY,
  APPLICATION_COMMENTS_QUERY_KEY,
  buildApplicationCommentsWithReplies,
  usePageStore,
  type ApplicationDetailComment,
} from "../application-detail-page.lib";

const SNACK_TITLES_CREATE_COMMENT = {
  pending: "Отправка комментария",
  fulfilled: "Комментарий отправлен",
  rejected: "Не удалось отправить комментарий",
} as const;

const fetchApplicationComments = async (
  findComments: typeof findExternalComments,
  requestId: string,
  signal?: AbortSignal,
): Promise<ApplicationDetailComment[]> => {
  const rootComments = (await findComments({ path: { sourceId: requestId, commentEntityId: requestId }, signal })).data;
  const repliesByRootId = new Map<string, CommentDto[]>();

  await Promise.all(
    rootComments
      .filter((comment) => comment.hasChildren)
      .map(async (comment) => {
        const replies = (
          await findComments({
            path: { sourceId: requestId, commentEntityId: requestId },
            query: { parentCommentId: comment.id },
            signal,
          })
        ).data;
        repliesByRootId.set(comment.id, replies);
      }),
  );

  return buildApplicationCommentsWithReplies(rootComments, repliesByRootId);
};

export const useApplicationComments = () => {
  const pageStore = usePageStore();
  const clientType = useClientType();
  const userInfo = useUserInfo();
  const queryClient = useQueryClient();

  const requestId = pageStore?.application?.requestId ?? "";
  const commentEntityName = pageStore?.application?.title ?? "Заявка";
  const resolvedClientType = clientType ?? "external";
  const queryKey = APPLICATION_COMMENTS_QUERY_KEY(requestId, resolvedClientType);

  const commentsQuery = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const findComments = clientType === "internal" ? findInternalComments : findExternalComments;
      return fetchApplicationComments(findComments, requestId, signal);
    },
    enabled: Boolean(requestId && clientType),
  });

  useEffect(() => {
    if (commentsQuery.data) {
      pageStore?.setComments(commentsQuery.data);
    }
  }, [commentsQuery.data, pageStore]);

  const submitCommentMutation = useMutation({
    mutationFn: async ({ text, parentCommentId }: { text: string; parentCommentId?: string }) => {
      if (!userInfo?.sub || !requestId || !clientType) {
        throw new Error("Missing user, request id, or client type");
      }

      const createComment = clientType === "internal" ? createInternalComment : createExternalComment;

      await createComment({
        body: {
          authorId: userInfo.sub,
          commentEntityId: requestId,
          commentEntityName,
          commentEntityType: "REQUEST",
          fileIds: [],
          mentionIds: [],
          sourceId: requestId,
          sourceType: "REQUEST",
          text: text.trim(),
          ...(parentCommentId ? { parentCommentId } : {}),
        },
      });
    },
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey }),
        queryClient.invalidateQueries({
          queryKey: APPLICATION_COMMENTS_COUNT_QUERY_KEY(requestId, resolvedClientType),
        }),
      ]);
      if (variables.parentCommentId) {
        pageStore?.clearActiveReplyId();
      }
    },
  });

  const submitComment = (text: string, parentCommentId?: string) => {
    const trimmedText = text.trim();
    if (!trimmedText || submitCommentMutation.isPending) {
      return;
    }

    setPromiseSnack({
      promise: submitCommentMutation.mutateAsync({ text: trimmedText, parentCommentId }),
      promiseSettings: {
        title: SNACK_TITLES_CREATE_COMMENT,
      },
    });
  };

  return {
    comments: commentsQuery.data ?? pageStore?.application?.comments ?? [],
    isLoading: commentsQuery.isLoading,
    isSubmitting: submitCommentMutation.isPending,
    submitComment,
  };
};
